import React, { useMemo } from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import SkuflixCrossIcon from "core/icons/skuflix/SkuflixCrossIcon";
import Box from "@mui/material/Box";
import { ICON_MAP } from "core/components/VodSeekBar/TimecodeMarkers";
import { usePlayerOccupiedZones } from "core/contexts/PlayerOccupiedZonesContext";

const PREFIX = "VideoAnnotation";

const classes = {
  root: `${PREFIX}-root`,
  card: `${PREFIX}-card`,
  header: `${PREFIX}-header`,
  icon: `${PREFIX}-icon`,
  content: `${PREFIX}-content`,
  closeButton: `${PREFIX}-closeButton`,
};

const Root = styled("div", {
  shouldForwardProp: (prop) =>
    prop !== "position" &&
    prop !== "offsetTop" &&
    prop !== "offsetRight" &&
    prop !== "offsetBottom" &&
    prop !== "offsetLeft",
})(({ theme, position, offsetTop, offsetRight, offsetBottom, offsetLeft }) => {
  const basePositionStyles = {
    TOP_LEFT: { top: theme.spacing(2) },
    TOP_RIGHT: { top: theme.spacing(2) },
    BOTTOM_LEFT: { bottom: theme.spacing(10) },
    BOTTOM_RIGHT: { bottom: theme.spacing(10) },
  };

  const baseStyle = basePositionStyles[position] || {};

  // Функция для парсинга значения из строки типа "80px" в число
  const parseValue = (value) => {
    if (typeof value === "string") {
      const match = value.match(/^(\d+(?:\.\d+)?)px$/);
      return match ? parseFloat(match[1]) : 0;
    }
    return typeof value === "number" ? value : 0;
  };

  // Применяем динамические отступы
  const dynamicStyle = {};
  if (offsetTop !== undefined && offsetTop > 0) {
    if (position === "TOP_LEFT" || position === "TOP_RIGHT") {
      const baseValue = parseValue(baseStyle.top);
      dynamicStyle.top = `${baseValue + offsetTop}px`;
    }
  }
  if (offsetRight !== undefined && offsetRight > 0) {
    if (position === "TOP_RIGHT" || position === "BOTTOM_RIGHT") {
      // Для right базовое значение устанавливается через медиа-запросы
      // Устанавливаем абсолютные значения с учетом offset для каждого размера экрана
      const baseMobile = parseValue(theme.spacing(1));
      const base375 = parseValue(theme.spacing(2));
      const baseMd = parseValue(theme.spacing(3));
      dynamicStyle.right = `${baseMobile + offsetRight}px`;
      dynamicStyle["@media (min-width: 375px)"] = {
        right: `${base375 + offsetRight}px`,
      };
      dynamicStyle[theme.breakpoints.up("md")] = {
        right: `${baseMd + offsetRight}px`,
      };
    }
  }
  if (offsetBottom !== undefined && offsetBottom > 0) {
    if (position === "BOTTOM_LEFT" || position === "BOTTOM_RIGHT") {
      const baseValue = parseValue(baseStyle.bottom);
      dynamicStyle.bottom = `${baseValue + offsetBottom}px`;
    }
  }
  if (offsetLeft !== undefined && offsetLeft > 0) {
    if (position === "TOP_LEFT" || position === "BOTTOM_LEFT") {
      // Для left базовое значение устанавливается через медиа-запросы
      // Устанавливаем абсолютные значения с учетом offset для каждого размера экрана
      const baseMobile = parseValue(theme.spacing(1));
      const base375 = parseValue(theme.spacing(2));
      const baseMd = parseValue(theme.spacing(3));
      dynamicStyle.left = `${baseMobile + offsetLeft}px`;
      dynamicStyle["@media (min-width: 375px)"] = {
        ...dynamicStyle["@media (min-width: 375px)"],
        left: `${base375 + offsetLeft}px`,
      };
      dynamicStyle[theme.breakpoints.up("md")] = {
        ...dynamicStyle[theme.breakpoints.up("md")],
        left: `${baseMd + offsetLeft}px`,
      };
    }
  }

  // На мобилке используем только вертикальное позиционирование (TOP/BOTTOM)
  const mobileStyle = {};
  if (position === "TOP_LEFT" || position === "TOP_RIGHT") {
    mobileStyle.top = baseStyle.top + (offsetTop || 0);
    mobileStyle.left = "50%";
    mobileStyle.right = "auto";
    mobileStyle.transform = "translateX(-50%)";
  } else if (position === "BOTTOM_LEFT" || position === "BOTTOM_RIGHT") {
    mobileStyle.bottom = baseStyle.bottom + (offsetBottom || 0);
    mobileStyle.left = "50%";
    mobileStyle.right = "auto";
    mobileStyle.transform = "translateX(-50%)";
  }

  const finalStyle = {
    position: "absolute",
    ...baseStyle,
    ...dynamicStyle,
    maxWidth: theme.spacing(43.75),
    width: `calc(100% - ${theme.spacing(4)})`,
    zIndex: 200,
    pointerEvents: "auto",
    animation: `fadeIn ${theme.transitions.duration.enteringScreen}ms ${theme.transitions.easing.easeIn}`,
    "@keyframes fadeIn": {
      from: {
        opacity: 0,
        transform: "translateY(-10px)",
      },
      to: {
        opacity: 1,
        transform: "translateY(0)",
      },
    },
    "@keyframes fadeInMobile": {
      from: {
        opacity: 0,
        transform: "translateX(-50%) translateY(-10px)",
      },
      to: {
        opacity: 1,
        transform: "translateX(-50%) translateY(0)",
      },
    },
    // Боковые отступы как у виджетов и рекламных кнопок
    ...(position === "TOP_LEFT" || position === "BOTTOM_LEFT"
      ? {
          left: theme.spacing(1),
          ["@media (min-width: 375px)"]: {
            left: theme.spacing(2),
          },
          [theme.breakpoints.up("md")]: {
            left: theme.spacing(3),
          },
        }
      : {}),
    ...(position === "TOP_RIGHT" || position === "BOTTOM_RIGHT"
      ? {
          right: theme.spacing(1),
          ["@media (min-width: 375px)"]: {
            right: theme.spacing(2),
          },
          [theme.breakpoints.up("md")]: {
            right: theme.spacing(3),
          },
        }
      : {}),
    [theme.breakpoints.down("sm")]: {
      ...mobileStyle,
      maxWidth: theme.spacing(30),
      width: `calc(100% - ${theme.spacing(2)})`,
      animation: `fadeInMobile ${theme.transitions.duration.enteringScreen}ms ${theme.transitions.easing.easeIn}`,
      transform: mobileStyle.transform || "none",
    },

    [`& .${classes.card}`]: {
      backgroundColor: "rgba(30, 30, 30, 0.95)",
      backdropFilter: "blur(10px)",
      borderRadius: theme.shape.borderRadius * 1.5,
      boxShadow: theme.shadows[4],
      position: "relative",
      [theme.breakpoints.down("sm")]: {
        borderRadius: theme.shape.borderRadius,
      },
    },

    [`& .${classes.header}`]: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
      [theme.breakpoints.down("sm")]: {
        gap: theme.spacing(0.375),
        marginBottom: theme.spacing(0.375),
      },
    },

    [`& .${classes.icon}`]: {
      width: theme.spacing(3),
      height: theme.spacing(3),
      borderRadius: "50%",
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      "& svg": {
        fontSize: theme.spacing(2),
        color: "white",
      },
      [theme.breakpoints.down("sm")]: {
        width: theme.spacing(1.75),
        height: theme.spacing(1.75),
        "& svg": {
          fontSize: theme.spacing(1.25),
        },
      },
    },

    [`& .${classes.content}`]: {
      "& a": {
        color: "#64B5F6",
        textDecoration: "underline",
        "&:hover": {
          color: "#90CAF9",
        },
      },
      "& p": {
        margin: 0,
        "&:not(:last-child)": {
          marginBottom: 8,
        },
      },
    },

    [`& .${classes.closeButton}`]: {
      position: "absolute",
      top: theme.spacing(1),
      right: theme.spacing(1),
      color: "rgba(255, 255, 255, 0.7)",
      "&:hover": {
        color: "white",
        backgroundColor: "rgba(255, 255, 255, 0.1)",
      },
      [theme.breakpoints.down("sm")]: {
        top: theme.spacing(0.25),
        right: theme.spacing(0.25),
        padding: theme.spacing(0.5),
        "& svg": {
          fontSize: theme.spacing(2),
        },
      },
    },
  };

  return finalStyle;
});

function VideoAnnotation({ annotation, onClose, index = 0 }) {
  const IconComponent = ICON_MAP[annotation.icon] || ICON_MAP.FLAG;
  const { zones } = usePlayerOccupiedZones();

  // Вычисляем динамические отступы на основе занятых зон
  const offsets = useMemo(() => {
    const { position } = annotation;
    let offsetTop = 0;
    let offsetRight = 0;
    let offsetBottom = 0;
    let offsetLeft = 0;

    // Базовый отступ между элементами
    const GAP = 16;
    // Дополнительный запас, чтобы не налезать на кнопку закрытия/угловые элементы
    const SAFE_GAP = 12;
    // Высота одной аннотации для стекирования (примерная)
    const ANNOTATION_HEIGHT = 120;

    switch (position) {
      case "TOP_LEFT":
        // Базовый безопасный отступ от краев
        offsetLeft += SAFE_GAP;
        offsetTop += SAFE_GAP;

        // Учитываем элементы в том же углу
        if (zones.TOP_LEFT.visible) {
          offsetLeft += zones.TOP_LEFT.width + GAP;
        }
        // Учитываем header
        if (zones.header.visible) {
          offsetTop += zones.header.height + GAP;
        }
        // Стекирование множественных аннотаций
        if (index > 0) {
          offsetTop += index * (ANNOTATION_HEIGHT + GAP);
        }
        break;

      case "TOP_RIGHT":
        // Базовый безопасный отступ от краев
        offsetRight += SAFE_GAP;
        offsetTop += SAFE_GAP;

        // Учитываем элементы в том же углу
        if (zones.TOP_RIGHT.visible) {
          offsetRight += zones.TOP_RIGHT.width + GAP;
        }
        // Учитываем header
        if (zones.header.visible) {
          offsetTop += zones.header.height + GAP;
        }
        // Стекирование множественных аннотаций
        if (index > 0) {
          offsetTop += index * (ANNOTATION_HEIGHT + GAP);
        }
        break;

      case "BOTTOM_LEFT":
        // Базовый безопасный отступ от краев
        offsetLeft += SAFE_GAP;
        offsetBottom += SAFE_GAP;

        // Учитываем элементы в том же углу
        if (zones.BOTTOM_LEFT.visible) {
          offsetLeft += zones.BOTTOM_LEFT.width + GAP;
        }
        // Учитываем footer
        if (zones.footer.visible) {
          offsetBottom += zones.footer.height + GAP;
        }
        // Стекирование множественных аннотаций
        if (index > 0) {
          offsetBottom += index * (ANNOTATION_HEIGHT + GAP);
        }
        break;

      case "BOTTOM_RIGHT":
        // Базовый безопасный отступ от краев
        offsetRight += SAFE_GAP;
        offsetBottom += SAFE_GAP;

        // Учитываем элементы в том же углу
        if (zones.BOTTOM_RIGHT.visible) {
          offsetRight += zones.BOTTOM_RIGHT.width + GAP;
        }
        // Учитываем footer
        if (zones.footer.visible) {
          offsetBottom += zones.footer.height + GAP;
        }
        // Стекирование множественных аннотаций
        if (index > 0) {
          offsetBottom += index * (ANNOTATION_HEIGHT + GAP);
        }
        break;

      default:
        break;
    }

    return { offsetTop, offsetRight, offsetBottom, offsetLeft };
  }, [annotation.position, zones, index]);

  return (
    <Root
      position={annotation.position}
      offsetTop={offsets.offsetTop}
      offsetRight={offsets.offsetRight}
      offsetBottom={offsets.offsetBottom}
      offsetLeft={offsets.offsetLeft}
    >
      <Card
        className={classes.card}
        sx={{
          backgroundColor: "rgba(30, 30, 30, 0.95) !important",
          backdropFilter: "blur(10px)",
        }}
      >
        <IconButton
          className={classes.closeButton}
          size="small"
          onClick={onClose}
          aria-label="Закрыть"
        >
          <SkuflixCrossIcon fontSize="small" />
        </IconButton>
        <CardContent
          sx={(theme) => ({
            p: 2,
            "&:last-child": {
              paddingBottom: theme.spacing(2),
            },
            [theme.breakpoints.down("sm")]: {
              p: 0.75,
              "&:last-child": {
                paddingBottom: theme.spacing(0.75),
              },
            },
          })}
        >
          <Box className={classes.header}>
            <Box className={classes.icon}>
              <IconComponent />
            </Box>
            <Typography
              variant="subtitle1"
              sx={(theme) => ({
                color: "white",
                fontWeight: 600,
                flex: 1,
                fontSize: "0.95rem",
                [theme.breakpoints.down("sm")]: {
                  fontSize: "0.65rem",
                  lineHeight: 1.2,
                },
              })}
            >
              {annotation.title}
            </Typography>
          </Box>
          {annotation.description && (
            <Typography
              variant="body2"
              className={classes.content}
              sx={(theme) => ({
                color: "rgba(255, 255, 255, 0.9)",
                fontSize: "0.875rem",
                lineHeight: 1.5,
                [theme.breakpoints.down("sm")]: {
                  fontSize: "0.6rem",
                  lineHeight: 1.25,
                },
              })}
              dangerouslySetInnerHTML={{ __html: annotation.description }}
            />
          )}
          {annotation.linkedContent && (
            <Box
              component="a"
              href={annotation.linkedContent.siteUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={(theme) => ({
                mt: 1,
                display: "flex",
                gap: 1,
                alignItems: "center",
                textDecoration: "none",
                cursor: "pointer",
                "&:hover": {
                  opacity: 0.85,
                },
                [theme.breakpoints.down("sm")]: {
                  gap: 0.5,
                  mt: 0.5,
                },
              })}
            >
              <Box
                component="img"
                src={
                  annotation.linkedContent.thumbnails?.[0]?.webpUrl ||
                  annotation.linkedContent.thumbnails?.[0]?.url
                }
                alt={annotation.linkedContent.title}
                sx={(theme) => ({
                  height: theme.spacing(6),
                  width: "auto",
                  borderRadius: "5px",
                  objectFit: "cover",
                  flexShrink: 0,
                  [theme.breakpoints.down("sm")]: {
                    height: theme.spacing(4),
                  },
                })}
              />
              <Typography
                variant="caption"
                sx={(theme) => ({
                  color: "rgba(255, 255, 255, 0.9)",
                  fontSize: "0.75rem",
                  lineHeight: 1.4,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  [theme.breakpoints.down("sm")]: {
                    fontSize: "0.55rem",
                    lineHeight: 1.2,
                  },
                })}
              >
                {annotation.linkedContent.title.length > 30
                  ? `${annotation.linkedContent.title.substring(0, 30)}...`
                  : annotation.linkedContent.title}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Root>
  );
}

VideoAnnotation.propTypes = {
  annotation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    icon: PropTypes.string.isRequired,
    position: PropTypes.oneOf([
      "TOP_LEFT",
      "TOP_RIGHT",
      "BOTTOM_LEFT",
      "BOTTOM_RIGHT",
    ]).isRequired,
    duration: PropTypes.number,
    linkedContent: PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      siteUrl: PropTypes.string,
      thumbnails: PropTypes.arrayOf(
        PropTypes.shape({
          format: PropTypes.string,
          url: PropTypes.string,
          webpUrl: PropTypes.string,
        })
      ),
    }),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  index: PropTypes.number,
};

export default VideoAnnotation;
